/**
 * Subscription Configuration
 *
 * Single source of truth for plan-based access decisions on the frontend.
 *
 * IMPORTANT — Design principles:
 *  1. The server is always the authoritative source for plan data.
 *     This file is a thin helper layer over what /api/me already returns.
 *     Do NOT duplicate plan limits or pricing here — those live in the DB.
 *
 *  2. Use AgencyMaxUsers (returned by /api/me) to gate agency-tier features.
 *     max_agents > 1  →  agency plan  →  agency admin UI visible.
 *     This is future-proof: adding new plans requires zero frontend changes.
 *
 *  3. Use user.featureFlags (returned by /api/me) for individual feature gates.
 *     Do NOT recompute feature eligibility on the frontend.
 *
 *  4. Role and plan are independent dimensions:
 *     - role === 'agentUser'  →  team member added by an admin (never sees admin UI)
 *     - AgencyMaxUsers > 1   →  agency-tier plan (admin UI is available to the owner)
 *     Both conditions must be satisfied to show agency admin features.
 */

// ─── Plan metadata (display only — not for access decisions) ─────────────────

/**
 * Human-readable plan names keyed by the canonical plan_id from the DB.
 * Used for UI labels, upgrade prompts, and billing pages.
 *
 * Add new plans here when they're added to the SubscriptionPlans table.
 * Access logic requires no changes — it reads AgencyMaxUsers from the API.
 */
export const PLAN_DISPLAY_NAMES = {
  solo:         'Solo',
  agentplus:    'Solo+',
  agency_lite:  'Agency Lite',
  agency:       'Agency',
  agencyplus:   'Agency+',
  enterprise:   'Enterprise',
};

/**
 * Plans that include multi-agent agency features.
 * Used for display logic only (e.g. upgrade prompts pointing to the right tier).
 * Access control uses AgencyMaxUsers, not this set.
 */
export const AGENCY_PLAN_IDS = new Set([
  'agency_lite',
  'agency',
  'agencyplus',
  'enterprise',
]);

// ─── Access helpers ───────────────────────────────────────────────────────────

/**
 * Returns true if this user's agency is on an agency-tier plan.
 * Gate: the server-supplied AgencyMaxUsers field (max_agents from SubscriptionPlans).
 *
 * @param {Object|null|undefined} user  The user object from /api/me
 * @returns {boolean}
 */
export const isAgencyPlan = (user) => {
  const maxUsers = user?.AgencyMaxUsers ?? 1;
  return maxUsers > 1;
};

/**
 * Returns true if this user should see the "My Agency" admin section.
 *
 * Two conditions must both be true:
 *  1. The user is NOT an agentUser (team member added to someone else's agency).
 *     agentUsers are agents, not owners — they never manage the agency.
 *  2. The agency is on a plan that includes a team (AgencyMaxUsers > 1).
 *     Solo-plan account owners have no team to manage.
 *
 * @param {Object|null|undefined} user  The user object from /api/me
 * @returns {boolean}
 */
export const canSeeAgencyAdmin = (user) => {
  if (!user) return false;
  const isSubAgent = user.role === 'agentUser';
  return !isSubAgent && isAgencyPlan(user);
};

/**
 * Returns the human-readable display name for the user's current plan.
 *
 * @param {Object|null|undefined} user  The user object from /api/me
 * @returns {string}
 */
export const getPlanDisplayName = (user) => {
  const planId = user?.planId ?? user?.AgencyPlan;
  return PLAN_DISPLAY_NAMES[planId] ?? planId ?? 'Unknown Plan';
};

/**
 * Returns true if a specific server-computed feature flag is enabled for this user.
 * Always prefer this over hardcoded tier checks in components.
 *
 * Supported flags (returned by /api/me):
 *  - branding               Can add logo/branding to itineraries
 *  - api                    API access enabled
 *  - impersonation          Admin impersonation of agents
 *  - extraUsers             Can add extra user seats
 *  - customQuestionnaireLink Can create custom public questionnaire links
 *
 * @param {Object|null|undefined} user  The user object from /api/me
 * @param {string}                flag  Feature flag key
 * @returns {boolean}
 */
export const hasFeatureFlag = (user, flag) => {
  return user?.featureFlags?.[flag] === true;
};
